// apps/client/src/services/plans.ts

import { PlanOption } from '@avanzu/types';

const API_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export class NoPaymentMethodError extends Error {
  code: string;

  constructor(message: string) {
    super(message);
    this.name = 'NoPaymentMethodError';
    this.code = 'no_payment_method';
  }
}

export class PaymentRequiresActionError extends Error {
  code = 'payment_requires_action';
  clientSecret: string | null;
  paymentIntentId: string | null;

  constructor(message: string, clientSecret: string | null, paymentIntentId: string | null) {
    super(message);
    this.name = 'PaymentRequiresActionError';
    this.clientSecret = clientSecret;
    this.paymentIntentId = paymentIntentId;
  }
}

export const fetchPlans = async (): Promise<PlanOption[]> => {
  const response = await fetch(`${API_URL}/plans`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch plans');
  }

  const data = await response.json();
  return data.plans || [];
};

/**
 * Upgrade the organisation's tier. Charges the price difference.
 */
export const upgradeTier = async (targetProductId: string): Promise<void> => {
  const organisationId = localStorage.getItem('organisationId') || '';
  if (!organisationId) {
    throw new Error('No organisation selected');
  }

  const response = await fetch(
    `${API_URL}/organisations/${organisationId}/stripe/tier/upgrade`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ targetProductId }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    if (errorData.code === 'payment_requires_action') {
      throw new PaymentRequiresActionError(
        errorData.error || 'Payment requires confirmation',
        errorData.clientSecret ?? null,
        errorData.paymentIntentId ?? null
      );
    }

    if (errorData.code === 'no_payment_method') {
      throw new NoPaymentMethodError(
        errorData.message || 'Please add a payment method before upgrading'
      );
    }

    throw new Error(errorData.error || 'Failed to upgrade tier');
  }
};
